import { Product } from 'pages/PrivateOffers/pages/Next/generic/api/types/Product';
import { findMatchingProduct } from 'pages/PrivateOffers/pages/Next/generic/utils/product/productUtils';
import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from 'vendor/material';
import { Button } from '@tackle-io/platform-ui';
import { ChevronDown, ChevronRight } from 'mdi-material-ui';
import { useRbac } from 'utils/rbac';
import {
  Marketplace,
  OfferType,
} from 'pages/PrivateOffers/pages/Next/generic/types/TackleOffer';
import { DateTime } from 'luxon';
import useOffersCanvasActions from './useOffersCanvasActions';
import { PaymentModel } from 'pages/PrivateOffers/pages/Next/aws/api/types/AwsPrivateOffer';
import { getUtcDateTimeFromSerializedIsoDateTime } from 'pages/PrivateOffers/pages/Next/generic/utils/date/dateUtils';
import { OffersCommonOfferResponse } from 'pages/PrivateOffers/pages/Next/generic/api/types/OffersCommonOfferResponse';
import { isOfferExpired } from 'pages/PrivateOffers/pages/Next/generic/utils/offer/offerUtils';
import MarketplaceLogo from 'pages/PrivateOffers/pages/Next/generic/MarketplaceLogo/MarketplaceLogo';

const getUsageDuration = (
  offer: OffersCommonOfferResponse,
  acceptedAtDate,
): number | null => {
  if (!offer.serviceEndAt) {
    return null;
  }
  const serviceEndDateTime = getUtcDateTimeFromSerializedIsoDateTime(
    offer.serviceEndAt,
  );
  const acceptedAtDateTime = getUtcDateTimeFromSerializedIsoDateTime(
    acceptedAtDate || DateTime.utc(),
  );
  return Math.ceil(
    Math.abs(
      acceptedAtDateTime.diff(serviceEndDateTime, ['days']).toObject()?.days,
    ),
  );
};

const determineTerms = (offer: OffersCommonOfferResponse): string | null => {
  if (offer.paymentModel === PaymentModel.PayGo) {
    return `${getUsageDuration(offer, offer.acceptedAt)} days`;
  }

  return null; // Temporary until we have the correct data to return something like "12 months"
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
  },
  createdBanner: {
    backgroundColor: theme.palette.NEUTRAL020,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.NEUTRAL030,
    },
  },
  value: {
    textTransform: 'capitalize',
  },
  menuPaper: {
    width: 160,
    maxWidth: 160,
  },
  chevron: {
    marginRight: theme.spacing(1),
  },
}));

const formatDate = (iso: string): string | undefined => {
  if (!iso) return undefined;
  const date = DateTime.fromISO(iso);
  if (!date.isValid) return undefined;
  return date.toLocaleString(DateTime.DATE_SHORT);
};

const LabelValue = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode | undefined;
}) => {
  const classes = useStyles();
  return (
    <Grid container direction="column">
      <Grid item xs={12}>
        <Typography
          variant="body2"
          style={{ fontFamily: 'Open Sans', fontWeight: 600 }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          variant="body2"
          className={classes.value}
          component="div"
          style={{ fontFamily: 'Open Sans', fontWeight: 400 }}
        >
          {value ?? '--'}
        </Typography>
      </Grid>
    </Grid>
  );
};

const MSFTOfferListViewItem = ({
  msftOffer,
  products,
  startExpanded = false,
}: {
  msftOffer: OffersCommonOfferResponse;
  products: Product[];
  startExpanded?: boolean;
}) => {
  const classes = useStyles();

  const { hasPermission } = useRbac();
  const { navigate } = useOffersCanvasActions();

  const [isExpanded, setIsExpanded] = useState(startExpanded);
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const currencyFormatter = new Intl.NumberFormat(
    undefined, // this should use the default locale
    {
      style: 'currency',
      currency: msftOffer?.currencyCode || 'USD',
    },
  );

  const productsByProductId = products.reduce(
    (acc, p) => ({
      ...acc,
      [p.productid]: p,
    }),
    {},
  );
  const product = findMatchingProduct(msftOffer.productId, productsByProductId);

  const handleEdit = () =>
    navigate({
      action: 'edit',
      domain: 'offers',
      cloud: 'azure',
      poId: msftOffer.privateOfferId,
    });
  const handleView = () =>
    navigate({
      action: 'view',
      domain: 'offers',
      cloud: 'azure',
      poId: msftOffer.privateOfferId,
    });

  const canEdit =
    hasPermission('offers:UpdateOffer') ||
    (hasPermission('offers:CreateDraftOffer') && !msftOffer?.offerRef);

  const offerIsExpired = isOfferExpired(msftOffer);
  const isCPPO = msftOffer?.offerType === OfferType.PartnerResale;
  const isExpiredCPPO = offerIsExpired && isCPPO;

  return (
    <div>
      <Box className={classes.createdBanner} onClick={toggleExpand}>
        {isExpanded ? (
          <ChevronDown className={classes.chevron} />
        ) : (
          <ChevronRight className={classes.chevron} />
        )}
        <Typography variant="body2" style={{ fontFamily: 'Open Sans' }}>
          <strong>Created {formatDate(msftOffer.createdAt)}</strong>
        </Typography>
      </Box>
      <Collapse in={isExpanded}>
        <Grid container className={classes.container} spacing={1}>
          <Grid item xs={12}>
            <LabelValue
              label="Offer name"
              value={
                <Grid container spacing={1}>
                  <Grid item>
                    <MarketplaceLogo
                      marketplace={Marketplace.Azure}
                      fontSize="small"
                    />
                  </Grid>
                  <Grid item style={{ fontFamily: 'Open Sans' }}>
                    {msftOffer.offerName}
                  </Grid>
                </Grid>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <LabelValue label="Product" value={product?.name} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LabelValue label="Status" value={msftOffer.status} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LabelValue label="Type" value={msftOffer.offerType} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LabelValue
              label="Last updated"
              value={formatDate(msftOffer.lastModifiedAt)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LabelValue
              label="Expires on"
              value={formatDate(msftOffer.offerExpirationAt)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LabelValue label="Terms" value={determineTerms(msftOffer)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LabelValue
              label="Gross"
              value={
                msftOffer?.totalContractValue &&
                currencyFormatter.format(
                  parseFloat(msftOffer.totalContractValue),
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {canEdit && !isExpiredCPPO && (
                <Grid item>
                  <Button
                    fullWidth
                    appearance="primary"
                    variant="outlined"
                    onClick={handleEdit}
                  >
                    Edit offer
                  </Button>
                </Grid>
              )}
              {hasPermission('offers:GetOffer') && (
                <Grid item xs>
                  <Button
                    fullWidth
                    variant="outlined"
                    appearance="primary"
                    onClick={handleView}
                  >
                    View offer
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Box mt={1}>
          <Divider variant="fullWidth" />
        </Box>
      </Collapse>
    </div>
  );
};

export default MSFTOfferListViewItem;
